import {
  AppChannel,
  PaymentInitiation,
  PaymentMethod,
  PaymentProvider,
  PaymentProviderId,
  PaymentRequest,
  PaymentReturn,
  PaymentVerification,
  ProviderCapabilities,
} from '../types';

/**
 * ─── MockProvider (100 % fonctionnel, aucun paiement réel) ──────────────────
 *
 * Reproduit fidèlement le flow FlexPay V2 pour tester toute l'application
 * sans fournisseur ni backend :
 *
 *   initiate → "page de paiement hébergée" interne (/payment/mock-gateway)
 *            → l'utilisateur y choisit le scénario : Payer / Refuser /
 *              Annuler / Laisser en attente
 *            → redirection vers la page de retour (approved/cancelled/declined)
 *            → verify simule le callback asynchrone (répond 'pending' deux
 *              fois avant le statut final, comme un vrai callback qui tarde).
 *
 * Le scénario choisi est stocké en sessionStorage, clé par orderNumber —
 * exactement comme un vrai provider garde l'état de la transaction chez lui.
 */

type MockScenario = 'success' | 'declined' | 'stay_pending';

interface MockTxRecord {
  reference: string;
  amount: number;
  currency: 'USD' | 'CDF';
  scenario: MockScenario | null;
  /** Nombre d'appels verify() déjà effectués (simule le délai du callback). */
  polls: number;
}

const STORE_KEY = 'mock-payment-transactions';
/** verify() répond 'pending' tant que polls < ce seuil (callback simulé). */
const POLLS_BEFORE_FINAL = 2;
const NETWORK_LATENCY_MS = 700;

function delay(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

function readStore(): Record<string, MockTxRecord> {
  try {
    return JSON.parse(sessionStorage.getItem(STORE_KEY) || '{}');
  } catch {
    return {};
  }
}

function writeStore(store: Record<string, MockTxRecord>): void {
  sessionStorage.setItem(STORE_KEY, JSON.stringify(store));
}

/** API interne utilisée par la page /payment/mock-gateway. */
export const mockGateway = {
  getTransaction(orderNumber: string): MockTxRecord | null {
    return readStore()[orderNumber] ?? null;
  },
  /** Enregistre le scénario choisi sur la fausse page de paiement. */
  chooseScenario(orderNumber: string, scenario: MockScenario): void {
    const store = readStore();
    const tx = store[orderNumber];
    if (!tx) return;
    tx.scenario = scenario;
    tx.polls = 0;
    writeStore(store);
  },
};

export class MockProvider implements PaymentProvider {
  readonly id = PaymentProviderId.MOCK;
  readonly displayName = 'Paiement simulé (test)';
  readonly capabilities: ProviderCapabilities = {
    // Le mock exécute n'importe quel moyen de paiement (tests tous canaux).
    methods: [
      PaymentMethod.BANK_CARD,
      PaymentMethod.MOBILE_MONEY,
      PaymentMethod.ORANGE_MONEY,
      PaymentMethod.MPESA,
    ],
    currencies: ['USD', 'CDF'],
    usesRedirect: true,
  };

  supportsChannel(_channel: AppChannel): boolean {
    return true; // le mock sert tous les canaux
  }

  async initiate(request: PaymentRequest): Promise<PaymentInitiation> {
    await delay(NETWORK_LATENCY_MS);

    const orderNumber = `MOCK-${Date.now().toString(36).toUpperCase()}`;
    const store = readStore();
    store[orderNumber] = {
      reference: request.reference,
      amount: request.plan.price,
      currency: request.plan.currency,
      scenario: null,
      polls: 0,
    };
    writeStore(store);

    // Équivalent de l'URL de page hébergée renvoyée par FlexPay V2.
    const url =
      `/payment/mock-gateway?orderNumber=${encodeURIComponent(orderNumber)}` +
      `&reference=${encodeURIComponent(request.reference)}`;
    return { kind: 'redirect', url, orderNumber };
  }

  async verify(orderNumber: string): Promise<PaymentVerification> {
    await delay(NETWORK_LATENCY_MS);

    const store = readStore();
    const tx = store[orderNumber];
    if (!tx) {
      return { status: 'not_found', transaction: null, message: 'Aucune transaction trouvée' };
    }

    const summary = {
      reference: tx.reference,
      orderNumber,
      amount: tx.amount,
      currency: tx.currency,
    };

    // Callback asynchrone simulé : 'pending' les premiers polls.
    tx.polls += 1;
    writeStore(store);
    if (tx.scenario === 'stay_pending' || tx.polls <= POLLS_BEFORE_FINAL) {
      return {
        status: 'pending',
        transaction: summary,
        message: 'Transaction en cours de traitement',
      };
    }

    if (tx.scenario === 'success') {
      return { status: 'success', transaction: summary, message: 'Transaction traitée avec succès' };
    }
    return { status: 'failed', transaction: summary, message: 'Transaction refusée par la banque (simulation)' };
  }

  parseReturn(params: URLSearchParams): PaymentReturn | null {
    if (params.get('provider') !== this.id) return null;
    const outcome = params.get('outcome');
    if (outcome !== 'approved' && outcome !== 'cancelled' && outcome !== 'declined') {
      return null;
    }
    return {
      outcome,
      reference: params.get('reference'),
      orderNumber: params.get('orderNumber'),
    };
  }
}
